import React, { memo } from "react";
import { Grid, GridSize, Box, TextField } from "@mui/material";
import { FormikProps, useFormikContext } from "formik";
import { get } from "lodash";
import { faAws, faMicrosoft } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Data } from "../../../../../store/terminal/types";
import { connect, useSelector } from "react-redux";
import { ConfigPropExtenderHoc } from "../../../../../common/StaticConfigProvider";
import UpdateSection from "../../../../../common/updateSection/UpdateSection";
import SectionWrapper from "../../../../../common/pageSections/SectionWrapper";
import TextInput from "../../../../../common/formElements/TextInput";
import ApplicationComponent from "./editTerminalApplicationComponent";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";
import { ApplicationState } from "../../../../../store";
import AutocompleteField from "../../../../../common/formElements/AutocompleteField";
import { isNil } from "ramda";

const shardText = (shard: number) => {
  const icon = shard < 2000 ? faAws : faMicrosoft;
  const text = shard < 2000 ? "" : "Microsoft Azure";
  const color = shard < 2000 ? "#FF5500" : "#0062ad";
  return shard >= 1000 ? (
    <Box display="flex" alignItems="center">
      <FontAwesomeIcon
        icon={icon}
        size="2x"
        color={color}
        style={{ marginRight: "5px" }}
      />
      {text}
    </Box>
  ) : (
    <></>
  );
};

interface FieldType {
  attribute: string;
  label: string;
}

interface FieldsMapProps {
  xs: GridSize;
  fields: FieldType[];
  disabled?: boolean;
  toolTips?: { terminals?: { options?: Record<string, string> } };
}

interface EditTerminalHostingProps {
  bulk: boolean;
  creatingTerminal: boolean;
  isUpdateAllowed?: boolean;
  toolTips?: { terminals?: { options?: Record<string, string> } };
  shouldHideField: (fieldName: string) => boolean;
}

interface EditTerminalHostingHocProps {
  bulk: boolean;
  creatingTerminal: boolean;
  isUpdateAllowed?: boolean;
  toolTips?: { terminals?: { options?: Record<string, string> } };
  shouldHideField: (fieldName: string) => boolean;
}

const FieldsMap: React.FC<FieldsMapProps> = ({ xs, fields, disabled, toolTips }) => (
  <>
    {fields.map(({ attribute, label }) => (
      <Grid item xs={xs} md={6} key={attribute}>
        <OptionalToolTip
          tooltip={get(
            toolTips,
            `terminals.options.hosting.["${attribute}"]`
          )}
        >
          <TextInput
            fieldname={`attributes.${attribute}`}
            label={label}
            disabled={disabled}
          />
        </OptionalToolTip>
      </Grid>
    ))}
  </>
);

interface Props {
  bulk: boolean;
  creatingTerminal: boolean;
  isUpdateAllowed?: boolean;
  toolTips?: { terminals?: { options?: Record<string, string> } };
  shouldHideField: (fieldName: string) => boolean;
}

function EditTerminalHostingComponent({
  bulk,
  creatingTerminal,
  isUpdateAllowed,
  toolTips,
  shouldHideField,
}: Props) {
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;
  let firmwareData = useSelector((state: ApplicationState) => state.myAxis?.firmwares);
  const firmwares = firmwareData && Array.isArray(firmwareData[values.attributes.Model]) ? firmwareData[values.attributes.Model] : []
  const isEnableFirmwareSelect = Boolean(
    ["QT850", "NG850"].includes(values.attributes.Model || "")
  );

  return bulk ? (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <SectionWrapper isChild={true} title="Terminal Update & Connectivity ( Hosting )">
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12}>
              <UpdateSection
                name="sections.hosting"
                disabled={!isUpdateAllowed}
              />
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <FieldsMap
                xs={12}
                fields={[
                  { attribute: "URL", label: "URL" },
                  { attribute: "URLBackup", label: "URL Backup" },
                ]}
                disabled={!isUpdateAllowed}
                toolTips={toolTips}
              />
            </Grid>
          </Grid>
        </SectionWrapper>
      </Grid>
      <Grid item xs={6}>
        <SectionWrapper isChild={true} title="Hosting Upgrade">
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12}>
              <UpdateSection
                name="sections.hostingUpgrade"
                disabled={!isUpdateAllowed}
              />
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <FieldsMap
                xs={12}
                fields={[
                  { attribute: "URLUpgrade", label: "URL Upgrade" },
                  {
                    attribute: "URLUpgradeBackup",
                    label: "URL Upgrade Backup",
                  },
                  {
                    attribute: "URLUpgradeFirmware",
                    label: "TERMINAL FIRMWARE UPGRADE URL",
                  },
                  // {
                  //   attribute: "URLUpgradeFirmwareBackup",
                  //   label: "Firmware Upgrade Backup URL",
                  // },
                  {
                    attribute: "DockerFirmware",
                    label: "Base Firmware URL",
                  },
                  // {
                  //   attribute: "DockerFirmwareBackup",
                  //   label: "Docker Firmware Backup URL",
                  // },
                ]}
                disabled={!isUpdateAllowed}
                toolTips={toolTips}
              />
            </Grid>
          </Grid>
        </SectionWrapper>
      </Grid>
    </Grid>
  ) : (
    <Grid item xs={12}>
      <SectionWrapper isChild={true} title="Terminal Update & Connectivity ( Hosting )">
        <Grid container alignItems="flex-start" spacing={3}>
          <Box display="flex" width="100%" flexDirection="column" m={2}>
            <Grid container alignItems="flex-start" spacing={3}>
              {creatingTerminal && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box flex={1} marginRight={2}>
                      <TextInput
                        fieldname="attributes.Shard"
                        label="Shard"
                        disabled={!isUpdateAllowed}
                      />
                    </Box>
                    <Box>
                      {values.attributes.Shard &&
                        shardText(values.attributes.Shard)}
                    </Box>
                  </Box>
                </Grid>
              )}
              <FieldsMap
                xs={12}
                fields={[
                  { attribute: "URL", label: "URL" },
                  { attribute: "URLBackup", label: "URL Backup" },
                  { attribute: "URLUpgrade", label: "URL Upgrade" },
                  {
                    attribute: "URLUpgradeBackup",
                    label: "URL Upgrade Backup",
                  },
                ]}
                disabled={!isUpdateAllowed}
                toolTips={toolTips}
              />
              {isEnableFirmwareSelect && (
                <Grid item xs={12} md={6}>
                  <OptionalToolTip tooltip="Set the version of the Firmware you want the terminal to be on ( Quest Terminals only )">
                    <AutocompleteField
                      key={`${values.attributes.Model}-${values.attributes.Manufacturer}-${firmwares.length}`}
                      fieldname="attributes.URLUpgradeFirmware"
                      onChange={(_event, value) => {
                        !isNil(value) && setFieldValue("attributes.URLUpgradeFirmware", value.downloadUrl);
                      }}
                      filterOptions={(options) => options}
                      value={firmwares.find((option) => option.downloadUrl === values.attributes.URLUpgradeFirmware)}
                      disableClearable={true}
                      getOptionLabel={(option) => option.version}
                      renderOption={(props, option) => (
                        <li {...props}>{option.version}</li>
                      )}
                      options={firmwares}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Firmware Version"
                        />
                      )}
                      disabled={!isUpdateAllowed}
                    />
                  </OptionalToolTip>
                </Grid>
              )}
              <Grid item xs={12} md={6} key={"URLUpgradeFirmware"}>
                <OptionalToolTip
                  tooltip={get(
                    toolTips,
                    `terminals.options.hosting.["URLUpgradeFirmware"]`
                  )}
                >
                  <TextInput
                    fieldname="attributes.URLUpgradeFirmware"
                    label="TERMINAL FIRMWARE UPGRADE URL"
                    disabled={!isUpdateAllowed || isEnableFirmwareSelect}
                  />
                </OptionalToolTip>
              </Grid>
              {/* <Grid item xs={12} md={6} key={"URLUpgradeFirmwareBackup"}>
                <OptionalToolTip
                  tooltip={get(
                    toolTips,
                    `terminals.options.hosting.["URLUpgradeFirmwareBackup"]`
                  )}
                >
                  <TextInput
                    fieldname="attributes.URLUpgradeFirmwareBackup"
                    label="Firmware Upgrade Backup URL"
                    disabled={!isUpdateAllowed}
                  />
                </OptionalToolTip>
              </Grid> */}
              <Grid item xs={12} md={6} key={"DockerFirmware"}>
                <OptionalToolTip
                  tooltip={get(
                    toolTips,
                    `terminals.options.hosting.["DockerFirmware"]`
                  )}
                >
                  <TextInput
                    fieldname="attributes.DockerFirmware"
                    label="Base Firmware URL"
                    disabled={!isUpdateAllowed}
                  />
                </OptionalToolTip>
              </Grid>
              {/* <Grid item xs={12} md={6} key={"DockerFirmwareBackup"}>
                <OptionalToolTip
                  tooltip={get(
                    toolTips,
                    `terminals.options.hosting.["DockerFirmwareBackup"]`
                  )}
                >
                  <TextInput
                    fieldname="attributes.DockerFirmwareBackup"
                    label="Docker Firmware Backup URL"
                    disabled={!isUpdateAllowed}
                  />
                </OptionalToolTip>
              </Grid> */}
            </Grid> 
          </Box>
        </Grid>
      </SectionWrapper>
      <ApplicationComponent isUpdateAllowed={isUpdateAllowed} shouldHideField={shouldHideField} />
    </Grid>
  );
}

const ConnectedEditTerminalHostingComponent = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: EditTerminalHostingHocProps) => {
    return {
      ...ownProps,
    };
  }
)(EditTerminalHostingComponent);

const ConnectedEditTerminalHostingHoc = (props: EditTerminalHostingProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedEditTerminalHostingComponent {...props} />
  </ConfigPropExtenderHoc>
);

export default memo(ConnectedEditTerminalHostingHoc);
